import React, { useState, useEffect } from "react";
import type { FC, PropsWithChildren } from "react";
import toast from "react-hot-toast";
import styles from "../../styles/Template.module.css";
import { useLocation } from "react-router-dom";

import { Button } from "../../components/Modal";
import RadioButton from "../../components/Modal/RadioButton";
import DisplayBox from "../../components/Modal/DisplayBox";
import InputField from "../../components/Modal/InputField";
import CategorySection from "./CategorySection";
import MultipleCategorySection from "./MultipleCategorySection";
import TemplateTable from "./TemplateTable";
import CategoryTable from "./CategoryTable";


import { QuestionWithErrors } from "./MultipleCategorySection";
import { useAddTemplate, useEditTemplate } from "../../hooks/rtmTemplate";
import {
  Category,
  FormData,
  IEditTemplateRes,
} from "../../hooks/rtmTemplate/index.type";
import e from "express";


type AddTemplateModalProps = PropsWithChildren<{
  loading?: boolean;
  onCloseModal?: () => void;
  onBack: () => void;
  fetchAllTemplate: () => void;
  templateId: string;
  setTemplateId: () => void;
  editTemplate: IEditTemplateRes | undefined;
  // fetchUpdateTemplate: () => void;
}>;
export interface FormErrors {
  name: string;
  type: string;
  groupedByCategory: string;
  categories: string;
}

const AddTemplateModal: FC<AddTemplateModalProps> = (props) => {
  const location = useLocation();
  const [selectedAnswerType, setSelectedAnswerType] = useState<string>("");
  const [isEditing, setIsEditing] = React.useState(false);
  const [formData, setFormData] = useState<FormData>(() => {
    try {
      const savedData = localStorage.getItem("template_form_data");
      return savedData
        ? JSON.parse(savedData)
        : {
            name: "",
            type: "",
            questionGroupedByCategory: "no",
            categories: [],
            questions: [],
          };
    } catch {
      return {
        name: "",
        type: "",
        questionGroupedByCategory: "no",
        categories: [],
        questions: [],
      };
    }
  });
 
  const [editingQuestion, setEditingQuestion] =
    React.useState<QuestionWithErrors>({
      text: "",
      questionId: "",
      categoryId: "",
      sequence: 0,
      answerType: "",
      option: [{ key: "", name: "", textboxRequired: "no" }],
      hasQuestionDependOnThis: "no",
      questionDependable: "no",
      dependableQuestionId: "",
      dependableQuestionOption: "",
      dependableQuestionType: "",
      errors: {},
      scale: {
        min: { key: "", name: "", textboxRequired: "no" },
        max: { key: "", name: "", textboxRequired: "no" },
      },
    });
  const [editCategory, setEditCategory] = React.useState<Category>({
    key: "",
    name: "",
    sequence: 0,
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({
    name: "",
    type: "",
    groupedByCategory: "",
    categories: "",
  });

  const [next, setNext] = useState(() => {
    const storedNext = localStorage.getItem("template_next");
    return storedNext ? JSON.parse(storedNext) : false;
  });

  const { addTemplate,addTemplateLoading} = useAddTemplate({ templateId: props.templateId });


  useEffect(() => {
    if (props.editTemplate) {
      setFormData({
        name: props.editTemplate?.data.templateData.name || "",
        type:
          props.editTemplate?.data.templateData.type === "intakeForm"
            ? "intakeForm"
            : "transmission",
        categories: props.editTemplate?.data.categoryList || [],
        questionGroupedByCategory:
          props.editTemplate?.data.templateData.questionGroupedByCategory ===
          "yes"
            ? "yes"
            : "no",
        questions: props.editTemplate?.data.questionList || [],
      });
    }
  }, [props.editTemplate]);

  useEffect(() => {
    localStorage.setItem("template_next", JSON.stringify(next));
  }, [next]);

  useEffect(() => {
    localStorage.setItem("template_form_data", JSON.stringify(formData));
  }, [formData]);

  useEffect(() => {
    const dependentIds = new Set(
      formData.questions
        .filter((q) => q.questionDependable === "yes" && q.dependableQuestionId)
        .map((q) => q.dependableQuestionId)
    );

    const questionsNeedUpdate = formData.questions.some(
      (question) =>
        (dependentIds.has(question.questionId) &&
          question.hasQuestionDependOnThis !== "yes") ||
        (!dependentIds.has(question.questionId) &&
          question.hasQuestionDependOnThis !== "no")
    );

    if (questionsNeedUpdate) {
      setFormData((prev) => ({
        ...prev,
        questions: prev.questions.map((question) => ({
          ...question,
          hasQuestionDependOnThis: dependentIds.has(question.questionId)
            ? "yes"
            : "no",
        })),
      }));
    }
  }, [formData.questions]);

  useEffect(() => {
    return () => {
      localStorage.removeItem("template_next");
      localStorage.removeItem("template_form_data");
    };
  }, [location.pathname]);

  const handleCancel = () => {
    setNext(false);
    setFormData((prev) => ({
      ...prev,
    }));
    setSelectedAnswerType("");
  };
  const handleToggleEditState = (state: boolean) => {
  setIsEditing(state);
};

  const handleAddTemplate = async () => {
  if (formData.questions.length === 0) {
    toast.error("Please add at least one question.");
    return;
  }

  try {
    await addTemplate(formData);
    props.setTemplateId();
    props.fetchAllTemplate();
    setNext(false);
    setFormData({
      name: "",
      type: "",
      questionGroupedByCategory: "no",
      categories: [],
      questions: [],
    });

    localStorage.removeItem("template_next");
    localStorage.removeItem("template_form_data");

    props.onCloseModal?.();
    props.onBack?.();
  } catch (error) {
    console.error("Failed to add template:", error);
  }
};

  const handleNextClick = () => {
    let hasError = false;
    const errors: any = {};

    // validate name
    if (!formData.name) {
      errors.name = "Template Name is required";
      hasError = true;
    }

    // validate type

    if (!formData.type) {
      errors.type = "Please select a template type";
      hasError = true;
    }
    if (!formData.questionGroupedByCategory) {
      errors.groupedByCategory = "Please select Yes or No";
      hasError = true;
    }
    if (
      formData.questionGroupedByCategory === "yes" &&
      formData.categories.length === 0
    ) {
      errors.categories = "Category cannot be empty";
      hasError = true;
    }

    if (hasError) {
      setFormErrors(errors);
      return;
    }

    setNext(true);
  };

  const handleDeleteQuestion = (key: string) => {
    setFormData((prevData) => {
      const remainingQuestions = prevData.questions.filter(
        (question) => question.questionId !== key
      );

      const updatedQuestions = remainingQuestions.map((q) => {
        if (q.dependableQuestionId === key) {
          return {
            ...q,
            questionDependable: "no" as const,
            dependableQuestionId: "",
            dependableQuestionOption: "",
            dependableQuestionType: "",
          };
        }
        return q;
      });

      return {
        ...prevData,
        questions: updatedQuestions,
      };
    });
  };

  const handleDeleteCategory = (key: string) => {
    setFormData((prevData) => ({
      ...prevData,
      categories: prevData.categories.filter(
        (category) => category.key !== key
      ),
    }));
  };

  const handleEditCategory = (key: string, name: string, sequence: number) => {
    setEditCategory({
      key,
      name,
      sequence,
    });
  };

  const handleEditQuestion = (questionId: string) => {
    const questionToEdit = formData.questions.find(
      (q) => q.questionId === questionId
    );
    if (questionToEdit) {
      setEditingQuestion(questionToEdit);
     handleToggleEditState(true)
    }
  };


  return (
    <div className={styles.container}>
      {/* Section 1 */}
      <div className={next ? styles.section_one_option : styles.section_1}>
        {next ? (
          // next === true: show name & type
          <div className={styles.optionhead}>
            <div>
              <span> Template Type : </span>
              <span>
                {formData.type
                  ?.replace(/([a-z])([A-Z])/g, "$1 $2")
                  .replace(/([A-Z])/g, " $1")
                  .split(" ")
                  .filter(Boolean)
                  .map(
                    (word) =>
                      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                  )
                  .join(" ")}
              </span>
            </div>
            <div>
              <span>Template Name : </span>
              <span>{formData.name}</span>
            </div>
          </div>
        ) : (
          // next === false: show radio buttons & input field
          <>
            <div className={styles.radioBtn_group}>
              <span className={styles.radio_btn_span}>Template Type:</span>
              <div className={styles.radio_btn_section}>
                <div
                  className={styles.radio_btn}
                  style={
                    props.editTemplate
                      ? { pointerEvents: "none", opacity: 0.6 }
                      : {}
                  }
                >
                  <RadioButton
                    label="Transmissions"
                    className={styles.templateType_label}
                    checked={formData.type === "transmission"}
                    onChange={() => {
                      setFormErrors({ ...formErrors, type: "" });
                      setFormData({ ...formData, type: "transmission" });
                    }}
                  />
                  <RadioButton
                    label="Intake Form"
                    checked={formData.type === "intakeForm"}
                    onChange={() => {
                      setFormData({ ...formData, type: "intakeForm" });
                      setFormErrors({ ...formErrors, type: "" });
                    }}
                    className={styles.templateType_label}
                  />
                </div>
                <span className={styles.radio_btn_error}>
                  {formErrors.type}
                </span>
              </div>
            </div>

            <div className={styles.inputField}>
              <span>Template Name:</span>
              <InputField
                value={formData.name}
                onChange={(e) => {
                  setFormData({ ...formData, name: e.target.value });
                  setFormErrors({ ...formErrors, name: "" });
                }}
                errorMessage={formErrors.name}
                errorClass={styles.error}
                className={styles.input}
                customClass={styles.input}
                containerClass={styles.template_name_ctn}
              />
            </div>
          </>
        )}
      </div>

      {/* Section 2 */}
      <div className={styles.section_2}>

        {next ? (
          <MultipleCategorySection
            formData={formData}
            setFormData={setFormData}
            next={next}
            setNext={setNext}
            selectedAnswerType={selectedAnswerType}
            setSelectedAnswerType={setSelectedAnswerType}
            editingQuestion={editingQuestion}
            handleToggleEditState={handleToggleEditState}
            isEditing={isEditing}
          />
        ) : (
          <CategorySection
            formData={formData}
            setFormData={setFormData}
            next={next}
            handleNextClick={handleNextClick}
            formErrors={formErrors}
            setFormErrors={setFormErrors}
            editCategory={editCategory}
            setEditCategory={setEditCategory}
            handleToggleEditState={handleToggleEditState}
            isEditing={isEditing}
          />
        )}
      </div>

      {/* Section 3 */}
      <div className={styles.section_3}>
        {!next ? (
          // <div className={styles.sub_section_categorylist}>
          //   {[...formData.categories]
          //     .sort((a, b) => Number(a.sequence) - Number(b.sequence))
          //     .map((category, index) => (
          //       <div
          //         key={category.key}
          //         draggable={true}
          //         onDragStart={onDragStart(index)}
          //         onDragOver={onDragOver(index)}
          //         onDrop={onDrop(index)}
          //         onDragLeave={onDragLeave(index)}
          //         onDragEnd={onDragEnd}
          //         style={{
          //           cursor: "move",
          //           marginBottom: "0.5rem",
          //           backgroundColor:
          //             dragOverIndex === index ? "#e0e0e0" : "transparent",
          //         }}
          //       >
          //         <DisplayBox
          //           text={category.name}
          //           icon={isEditMode ? undefined : "delete"} // Hide delete button when editing
          //           isEdit={props.templateId ? true : false}
          //           icon1="edit-2"
          //           className={`${styles.category_box} ${styles.categoryTag}`}
          //           iconClass={styles.categoryIcon}
          //           onClick={() => handleDeleteCategory(category.key)}
          //           isDraggble={true}
          //           dragIcon="move"
          //           onEdit={() =>
          //             handleEditCategory(
          //               category.key,
          //               category.name,
          //               category.sequence || 0
          //             )
          //           }
          //           style={{ color: "black" }}
          //         />
          //       </div>
          //     ))}
          // </div>
          <CategoryTable
          formData={formData}
          handleEditQuestion={handleEditCategory}
          handleDeleteQuestion={handleDeleteCategory}
         setFormData={setFormData}
          />
          
        ) : (
          <TemplateTable
            onCancel={handleCancel}
            onSubmit={handleAddTemplate}
            formData={formData}
            setFormData={setFormData}
            handleDeleteQuestion={handleDeleteQuestion}
            handleEditQuestion={handleEditQuestion}
            isEditing={Boolean(props.editTemplate)}
          />
        )}
      </div>

      {/* Buttons */}
      {next && (
        <div className={styles.buttonContainer}>
          <Button
            label="Back"
            icon="icon-back"
            span={false}
            onClick={handleCancel}
            customClass={styles.add_btn}
            iconStyle={{ height: "4rem", width: "2rem" }}
            submitButtonStyle={{
              display: "flex",
              flexDirection: "row",
              marginLeft: 0,
              gap:'1 rem'
          
            }}
          />
<Button
  label={
    addTemplateLoading
      ? props.editTemplate
        ? "Updating..."
        : "Saving..."
      : props.editTemplate
      ? "Update"
      : "Save"
  }
  icon={
    addTemplateLoading
      ? "" // no icon while loading
      : props.editTemplate
      ? "icon-edit-3" 
      : "icon-save" 
  }
  disabled={addTemplateLoading}
  span={false}
  customClass={styles.add_btn}
  iconStyle={{
    height: "1.5rem",
    width: "2rem",
    fill: "white",
    marginLeft: "0.5rem",
  }}
  onClick={handleAddTemplate}
/>

        </div>
      )}
    </div>
  );
};

export default AddTemplateModal;
